import { Component, OnChanges, OnInit, inject, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { LocalizationService } from '../../../core/localization/localization.service';
import { WorkflowTriggerOperatorConfig } from '../workflow-catalog.models';
import { parseConfigJson, parseStringMapping, stringifyConfigJson } from './workflow-operator-config.utils';
import { WorkflowVariableMappingGridComponent } from './workflow-variable-mapping-grid.component';

@Component({
  standalone: true,
  selector: 'ain-workflow-trigger-config-panel',
  imports: [FormsModule, InputTextModule, WorkflowVariableMappingGridComponent],
  template: `
    <section class="operator-panel">
      <h5>{{ t('workflow.designer.operators.trigger.title') }}</h5>

      @if (triggerType() === 'Schedule') {
        <label>{{ t('workflow.designer.operators.trigger.intervalMinutes') }}</label>
        <input pInputText type="number" min="1" max="1440" [(ngModel)]="model.intervalMinutes" (ngModelChange)="emitConfig()" />
        <p class="hint">{{ t('workflow.designer.operators.trigger.scheduleHint') }}</p>
      }

      @if (triggerType() === 'Event' || triggerType() === 'System' || triggerType() === 'Api') {
        <label>{{ t('workflow.designer.operators.trigger.eventTopic') }}</label>
        <input pInputText [(ngModel)]="model.eventTopic" (ngModelChange)="emitConfig()" />
      }

      @if (triggerType() === 'Message') {
        <label>{{ t('workflow.designer.operators.trigger.messageTopic') }}</label>
        <input pInputText [(ngModel)]="model.messageTopic" (ngModelChange)="emitConfig()" />
      }

      @if (triggerType() === 'AiSignal') {
        <label>{{ t('workflow.designer.operators.trigger.aiSignalType') }}</label>
        <input pInputText [(ngModel)]="model.aiSignalType" (ngModelChange)="emitConfig()" />
      }

      @if (triggerType() !== 'Manual') {
        <h6>{{ t('workflow.designer.operators.trigger.variableMapping') }}</h6>
        <ain-workflow-variable-mapping-grid
          [mapping]="model.variableMapping ?? {}"
          (mappingChange)="onVariableMappingChanged($event)" />
      }
    </section>
  `,
  styles: [`
    .operator-panel { display: flex; flex-direction: column; gap: 8px; margin-top: 8px; }
    .operator-panel h5, .operator-panel h6 { margin: 8px 0 4px; font-size: 13px; color: #334155; }
    .operator-panel label { font-size: 12px; color: #475569; }
    .hint { font-size: 12px; color: #64748b; margin: 0; }
  `]
})
export class WorkflowTriggerConfigPanelComponent implements OnInit, OnChanges {
  private readonly localization = inject(LocalizationService);

  readonly triggerType = input('Manual');
  readonly configJson = input('{}');
  readonly configJsonChange = output<string>();

  model: WorkflowTriggerOperatorConfig = {};

  ngOnInit(): void {
    this.loadFromConfig(this.configJson());
  }

  ngOnChanges(): void {
    this.loadFromConfig(this.configJson());
  }

  onVariableMappingChanged(mapping: Record<string, string>): void {
    this.model.variableMapping = mapping;
    this.emitConfig();
  }

  emitConfig(): void {
    this.configJsonChange.emit(stringifyConfigJson(this.model));
  }

  private loadFromConfig(raw: string): void {
    this.model = parseConfigJson<WorkflowTriggerOperatorConfig>(raw, { variableMapping: {} });
    this.model.variableMapping = parseStringMapping(this.model.variableMapping);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
